import React, { useState, useRef, useEffect } from "react";
import {
  ChatMessage,
  CalendarEvent,
  CaseFile,
  Note,
  LegalPayment,
  ChatSource,
  UserData,
} from "../types";
import { getLegalAdvice, generateEmbeddings } from "../services/geminiService";
import { db } from "../services/databaseService";
import { RateLimitService } from "../services/rateLimitService";

interface LegalChatProps {
  events: CalendarEvent[];
  cases: CaseFile[];
  notes: Note[];
  payments: LegalPayment[];
  user: UserData;
  onUpdateUser: (data: Partial<UserData>) => void;
  checkAiLimit: () => Promise<boolean>;
}

const LegalChat: React.FC<LegalChatProps> = ({
  events,
  cases,
  notes,
  payments,
  user,
  onUpdateUser,
  checkAiLimit,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: Date.now().toString(),
      role: "model",
      content:
        "Bună ziua. Sunt LexSmart AI. Folosesc conexiunea directă cu legislatie.just.ro pentru a vă oferi răspunsuri bazate pe Codurile la zi. Cu ce vă pot ajuta în legătură cu legislația sau dosarele dumneavoastră?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    // Verificăm limita înainte de a trimite (folosind funcția asincronă din Dashboard)
    const isAllowed = await checkAiLimit();
    if (!isAllowed) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    // Incrementăm utilizarea pe server
    await RateLimitService.incrementUsage('aiCalls');

    try {
      // --- RAG: Căutare Semantică în Documente ---
      let ragContext = "";
      try {
        const queryEmbedding = await generateEmbeddings(input);
        if (queryEmbedding && queryEmbedding.length > 0) {
          const relevantChunks = await db.searchDocumentChunks(queryEmbedding, 3, 0.4); // top 3 rezultate, prag de similaritate minim 40%
          if (relevantChunks && relevantChunks.length > 0) {
            ragContext = "Fragmente relevante din documentele utilizatorului:\n" + 
              relevantChunks.map((c, i) => `[Fragment ${i + 1}]: ${c.content}`).join('\n\n');
          }
        }
      } catch (err) {
        console.warn("Eroare la RAG:", err);
      }
      
      const response = await getLegalAdvice(input, {
        events,
        cases,
        notes,
        payments,
        user: { name: user.name, bar: user.bar },
        ragContext: ragContext || "Niciun document relevant găsit."
      });

      const groundingChunks =
        response.candidates?.[0]?.groundingMetadata?.groundingChunks;
      const sources: ChatSource[] = [];

      if (groundingChunks) {
        groundingChunks.forEach((chunk: any) => {
          if (chunk.web) {
            const uri = chunk.web.uri || "";
            const isOfficial =
              uri.includes("just.ro") || uri.includes("legislatie.just.ro");
            sources.push({
              id: Date.now().toString() + Math.random().toString(),
              title: isOfficial
                ? `🏛️ JURIDIC: ${chunk.web.title || "Portal Legislativ"}`
                : chunk.web.title || uri,
              url: uri,
              type: isOfficial ? "law" : "case",
            });
          }
        });
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString() + Math.random().toString(),
          role: "model",
          content:
            response.text ||
            "Nu am putut genera un răspuns legal valid. Te rog să reformulezi.",
          timestamp: new Date(),
          sources: sources.length > 0 ? sources : undefined,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString() + Math.random().toString(),
          role: "model",
          content:
            "Eroare la accesarea bazei de date legislative. Verificați conexiunea la internet.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white md:bg-slate-50">
      <div
        className="flex-grow p-4 md:p-10 overflow-y-auto space-y-4 md:space-y-6 custom-scrollbar"
        ref={scrollRef}
      >
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[95%] md:max-w-[85%] p-5 md:p-8 rounded-[2rem] shadow-sm ${
                msg.role === "user"
                  ? "bg-indigo-600 text-white rounded-tr-none shadow-indigo-100"
                  : "bg-white border border-slate-100 text-slate-800 rounded-tl-none"
              }`}
            >
              <div className="text-xs md:text-[15px] font-medium leading-relaxed italic whitespace-pre-wrap">
                {msg.content}
              </div>

              {msg.sources && msg.sources.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500 italic">
                      Verificat pe legislatie.just.ro:
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {msg.sources.map((s, i) => (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all border ${
                          msg.role === "user"
                            ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
                            : "bg-indigo-50 border-indigo-100 text-indigo-600 hover:border-indigo-300"
                        }`}
                      >
                        {s.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <p
                className={`text-[8px] mt-4 font-black uppercase tracking-widest opacity-40 ${msg.role === "user" ? "text-right" : "text-left"}`}
              >
                LexSmart AI Protocol •{" "}
                {msg.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-none px-6 py-4 flex gap-2 items-center shadow-sm">
              <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest italic animate-pulse">
                Consult legislatia...
              </span>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 md:p-8 bg-white border-t border-slate-100 shrink-0">
        <div className="max-w-5xl mx-auto flex items-end bg-slate-50 rounded-[2.5rem] border border-slate-200 overflow-hidden focus-within:border-indigo-600 transition-all p-1.5">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Introduceți întrebarea juridică (ex: 'Care sunt termenele de apel în civil?')..."
            className="flex-grow p-4 md:p-6 bg-transparent border-none outline-none resize-none text-sm md:text-base h-16 md:h-24 font-medium italic text-slate-900"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="w-14 h-14 md:w-20 md:h-20 bg-indigo-600 text-white rounded-[2rem] flex items-center justify-center transition-all disabled:opacity-50 shrink-0 shadow-xl active:scale-95 hover:bg-indigo-700 m-1"
          >
            <span className="text-2xl md:text-3xl">⚖️</span>
          </button>
        </div>
        <div className="text-center mt-4">
          <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest italic flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
            Conectat la Monitorul Oficial via legislatie.just.ro
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalChat;
